import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CreditCard, 
  DollarSign, 
  Calendar, 
  FileText, 
  CheckCircle,
  AlertCircle,
  User,
  ArrowLeft,
  Calculator,
  History,
  Ban,
  Loader2
} from 'lucide-react';
import { useMakePaymentMutation, useGetPaymentsByLoanQuery } from '../../services/loanPaymentApiSlice';
import { useGetAllocationHistoryQuery } from '../../services/loanPaymentAllocationApiSlice';

const MakePayment = () => {
  const navigate = useNavigate();
  const [makePayment, { isLoading: isSubmitting }] = useMakePaymentMutation();
  
  // Form state
  const [paymentData, setPaymentData] = useState({
    paymentId: `PAY-${Date.now()}`,
    scheduleId: '',
    installmentNo: '',
    monthlyAmount: '',
    overdueAmount: '',
    totalAmount: '0.00',
    paymentMethod: '',
    referenceNo: '',
    remarks: '',
    loanId: ''
  });

  // Additional state variables
  const [prefilledData, setPrefilledData] = useState(null);
  const [allocationHistory, setAllocationHistory] = useState([]);
  const [totalPaidSoFar, setTotalPaidSoFar] = useState(0);
  const [remainingTotalDue, setRemainingTotalDue] = useState(0);
  const [remainingFeesDue, setRemainingFeesDue] = useState(0);
  const [maxMonthlyAmount, setMaxMonthlyAmount] = useState(0);
  const [maxOverdueAmount, setMaxOverdueAmount] = useState(0);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [fetchedPayments, setFetchedPayments] = useState([]);

  // API queries
  const { data: paymentsData = [], isLoading: paymentsLoading } = useGetPaymentsByLoanQuery(
    prefilledData?.loanId, 
    { skip: !prefilledData?.loanId }
  );

  // State for allocation history queries
  const [allocationQueries, setAllocationQueries] = useState([]);

  const paymentMethods = [
    { value: '', label: 'Select Payment Method' },
    { value: 'Cash', label: 'Cash' },
    { value: 'Credit Card', label: 'Credit Card' },
    { value: 'Debit Card', label: 'Debit Card' },
    { value: 'Bank Transfer', label: 'Bank Transfer' },
    { value: 'Check', label: 'Check' },
    { value: 'Online Wallet', label: 'Online Wallet' }
  ];

  // Load data from localStorage on component mount
  useEffect(() => {
    const storedPayment = localStorage.getItem('pendingPayment');
    
    if (storedPayment) {
      try {
        const data = JSON.parse(storedPayment);
        setPrefilledData(data);
        
        // Check if we have all required data for automatic processing
        if (data.scheduleId && data.loanId) {
          setIsManualEntry(false);
          // Initialize form with all data from localStorage
          const monthlyAmount = (data.totalDue || 0).toFixed(2);
          const overdueAmount = (data.feesDue || 0).toFixed(2);
          const totalAmount = (parseFloat(monthlyAmount) + parseFloat(overdueAmount)).toFixed(2);
          
          setPaymentData(prev => ({
            ...prev,
            scheduleId: data.scheduleId || '',
            installmentNo: data.installmentNo || '',
            loanId: data.loanId || '',
            monthlyAmount: monthlyAmount,
            overdueAmount: overdueAmount,
            totalAmount: totalAmount
          }));
          
          // Set remaining amounts and max values
          setRemainingTotalDue(data.totalDue || 0);
          setRemainingFeesDue(data.feesDue || 0);
          setMaxMonthlyAmount(data.totalDue || 0);
          setMaxOverdueAmount(data.feesDue || 0);
        } else {
          setIsManualEntry(true);
          setPaymentData(prev => ({
            ...prev,
            scheduleId: data.scheduleId || '',
            installmentNo: data.installmentNo || '',
            loanId: data.loanId || '',
            monthlyAmount: '0.00',
            overdueAmount: '0.00'
          }));
        }
      } catch (error) {
        console.error('Error parsing stored payment data:', error);
        setIsManualEntry(true);
      }
    } else {
      // No pending payment - manual entry mode
      setIsManualEntry(true);
    }
  }, []);

  // When payments data is loaded, process each payment to get allocation history
  useEffect(() => {
    if (paymentsData.length > 0 && prefilledData?.scheduleId) {
      setFetchedPayments(paymentsData);
      processPaymentsAllocation(paymentsData);
    } else if (prefilledData?.loanId && !paymentsLoading) {
      // No payments found for this loan
      setLoadingAllocations(false);
      setPaymentData(prev => ({
        ...prev,
        monthlyAmount: (prefilledData.totalDue || 0).toFixed(2),
        overdueAmount: (prefilledData.feesDue || 0).toFixed(2),
      }));
      setRemainingTotalDue(prefilledData.totalDue || 0);
      setRemainingFeesDue(prefilledData.feesDue || 0);
      setMaxMonthlyAmount(prefilledData.totalDue || 0);
      setMaxOverdueAmount(prefilledData.feesDue || 0);
    }
  }, [paymentsData, paymentsLoading, prefilledData]);

  // Process payments and fetch allocation history for each
  const processPaymentsAllocation = async (payments) => {
    setLoadingAllocations(true);
    
    try {
      let totalPaidAmount = 0;
      let totalToOverdue = 0;
      const relevantAllocations = [];

      // For each payment, we need to fetch allocation history
      // Note: In a real app, you might want to batch these requests
      // or have a backend endpoint that returns all allocations for a loan/schedule
      for (const payment of payments) {
        if (payment.paymentId) {
          try {
            // Fetch allocation history for this payment and schedule
            const response = await fetch(
              `https://moneylend.dockyardsoftware.com/PaymentAllocation/GetAllocationHistory?paymentId=${payment.paymentId}&scheduleId=${prefilledData.scheduleId}`,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
            
            if (response.ok) {
              const data = await response.json();
              if (data?.ResultSet && data.ResultSet.length > 0) {
                relevantAllocations.push(...data.ResultSet);
                
                // Sum up paidAmount and toOverdue
                data.ResultSet.forEach(allocation => {
                  totalPaidAmount += parseFloat(allocation.PaidAmount) || 0;
                  totalToOverdue += parseFloat(allocation.ToOverdue) || 0;
                });
              }
            }
          } catch (error) {
            console.error('Error fetching allocation history:', error);
          }
        }
      }

      setAllocationHistory(relevantAllocations);
      setTotalPaidSoFar(totalPaidAmount + totalToOverdue);
      
      // Get original amounts from pending payment
      const originalTotalDue = parseFloat(prefilledData.totalDue) || 0;
      const originalFeesDue = parseFloat(prefilledData.feesDue) || 0;
      
      // Check if payment is already complete
      if (totalPaidAmount >= originalTotalDue && totalToOverdue >= originalFeesDue) {
        setIsPaymentComplete(true);
        setErrorMessage('Payment for this schedule is already complete. No remaining balance.');
        
        setRemainingTotalDue(0);
        setRemainingFeesDue(0);
        setMaxMonthlyAmount(0);
        setMaxOverdueAmount(0);
        
        setPaymentData(prev => ({
          ...prev,
          monthlyAmount: '0.00',
          overdueAmount: '0.00',
          totalAmount: '0.00'
        }));
      } else {
        // Calculate remaining amounts
        const remainingTotal = Math.max(0, originalTotalDue - totalPaidAmount);
        const remainingFees = Math.max(0, originalFeesDue - totalToOverdue);
        
        setRemainingTotalDue(remainingTotal);
        setRemainingFeesDue(remainingFees);
        setMaxMonthlyAmount(remainingTotal);
        setMaxOverdueAmount(remainingFees);
        
        // Prefill the form with remaining amounts
        setPaymentData(prev => ({
          ...prev,
          monthlyAmount: remainingTotal > 0 ? remainingTotal.toFixed(2) : '0.00',
          overdueAmount: remainingFees > 0 ? remainingFees.toFixed(2) : '0.00',
          totalAmount: (remainingTotal + remainingFees).toFixed(2)
        }));
      }
    } catch (error) {
      console.error('Error processing allocations:', error);
      setErrorMessage('Failed to load payment history. Please try again.');
    } finally {
      setLoadingAllocations(false);
    }
  };

   // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'monthlyAmount' || name === 'overdueAmount') {
      const numValue = parseFloat(value) || 0;
      
      // Check if value is negative
      if (numValue < 0) {
        setErrorMessage('Negative values are not allowed for payment amount');
      }
      // Check if value is equal to 0
      else if (numValue === 0 && value !== '') {
        setErrorMessage(`${name === 'monthlyAmount' ? 'Monthly' : 'Overdue'} amount must be greater than 0`);
      }
      // Check if value exceeds maximum allowed
      else if (name === 'monthlyAmount' && numValue > maxMonthlyAmount && !isManualEntry) {
        setErrorMessage(`Monthly amount cannot exceed ${formatCurrency(maxMonthlyAmount)}`);
      } else if (name === 'overdueAmount' && numValue > maxOverdueAmount && !isManualEntry) {
        setErrorMessage(`Overdue amount cannot exceed ${formatCurrency(maxOverdueAmount)}`);
      } else {
        setErrorMessage('');
      }
    }
    
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Calculate total whenever monthly or overdue amounts change
  useEffect(() => {
    const monthly = parseFloat(paymentData.monthlyAmount) || 0;
    const overdue = parseFloat(paymentData.overdueAmount) || 0;
    const total = (monthly + overdue).toFixed(2);
    
    setPaymentData(prev => ({
      ...prev,
      totalAmount: total
    }));
  }, [paymentData.monthlyAmount, paymentData.overdueAmount]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if payment is already complete
    if (isPaymentComplete) {
      alert('Payment for this schedule is already complete.');
      return;
    }
    
    // Check for validation errors
    if (errorMessage) {
      alert(errorMessage);
      return;
    }
    
     // Validate amounts for automatic mode
    if (!isManualEntry) {
      const monthlyAmount = parseFloat(paymentData.monthlyAmount) || 0;
      const overdueAmount = parseFloat(paymentData.overdueAmount) || 0;
      
      if (monthlyAmount <= 0) {
        alert('Monthly amount must be greater than 0');
        return;
      }
      
      if (overdueAmount < 0) {
        alert('Overdue amount must be greater than or equal to 0');
        return;
      }
      
      if (monthlyAmount > maxMonthlyAmount) {
        alert(`Monthly amount cannot exceed ${formatCurrency(maxMonthlyAmount)}`);
        return;
      }
      
      if (overdueAmount > maxOverdueAmount) {
        alert(`Overdue amount cannot exceed ${formatCurrency(maxOverdueAmount)}`);
        return;
      }
    }
    
    // Validate amounts for manual mode
    if (isManualEntry) {
      const monthlyAmount = parseFloat(paymentData.monthlyAmount) || 0;
      const overdueAmount = parseFloat(paymentData.overdueAmount) || 0;
      
      if (monthlyAmount <= 0) {
        alert('Monthly amount must be greater than 0');
        return;
      }
      
      if (overdueAmount < 0) {
        alert('Overdue amount must be greater than or equal to 0');
        return;
      }
    }
    
    try {
      const monthlyAmount = parseFloat(paymentData.monthlyAmount) || 0;
      const overdueAmount = parseFloat(paymentData.overdueAmount) || 0;
      const totalAmount = monthlyAmount + overdueAmount;
      
      if (totalAmount <= 0) {
        alert('Payment amount must be greater than zero');
        return;
      }
      
      // Prepare payload for API
      const payload = {
        scheduleId: paymentData.scheduleId,
        installmentNo: parseInt(paymentData.installmentNo) || 1,
        loanId: paymentData.loanId,
        paidAmount: totalAmount,
        paymentMethod: paymentData.paymentMethod,
        referenceNo: paymentData.referenceNo || `REF-${Date.now()}`,
        remarks: paymentData.remarks
      };

      // Call the API
      const response = await makePayment(payload).unwrap();
      
      // Show success message
      alert('Payment processed successfully!');
      
      // Clear pending payment from localStorage
      localStorage.removeItem('pendingPayment');
      
      // Navigate to payment history
      navigate('/admin/loanPayments');
      
    } catch (error) {
      console.error('Payment submission error:', error);
      alert(`Payment failed: ${error.data?.message || error.message || 'Unknown error'}`);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Check if payment button should be disabled
  const isPaymentButtonDisabled = () => {
    // Disable button if there's no pending payment in localStorage
    const storedPayment = localStorage.getItem('pendingPayment');
    if (!storedPayment) return true;
    
    if (isPaymentComplete) return true;
    if (errorMessage) return true;
    if (isSubmitting) return true;
    if (loadingAllocations) return true;
    
    const totalAmount = parseFloat(paymentData.totalAmount) || 0;
    if (totalAmount <= 0) return true;
    
    const monthlyAmount = parseFloat(paymentData.monthlyAmount) || 0;
    const overdueAmount = parseFloat(paymentData.overdueAmount) || 0;
    
    if (monthlyAmount <= 0) return true;
    if (overdueAmount < 0) return true;
    
    if (!isManualEntry) {
      if (monthlyAmount > maxMonthlyAmount) return true;
      if (overdueAmount > maxOverdueAmount) return true;
    }
    
    return false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-6 h-6 text-gray-600" />
              </button>
              <div className="p-2 bg-blue-600 rounded-lg">
                <DollarSign className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
                Make Payment
              </h1>
            </div>
            <div className="flex gap-2">
              {prefilledData && !isManualEntry && (
                <div className="text-sm bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-blue-700 font-medium">Auto-filled from loan</p>
                </div>
              )}
              {isManualEntry && (
                <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-700 font-medium">Manual Entry Mode</p>
                </div>
              )}
            </div>
          </div>
          <p className="text-gray-600 ml-14">
            {isManualEntry 
              ? 'Enter payment details manually' 
              : 'Complete the form below to process your loan payment'}
          </p>
        </div>

        {/* 👇 NEW: Alert when manual entry is enabled */}
        {isManualEntry && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-xl">
            <div className="flex items-center gap-2 text-amber-800">
              <AlertCircle className="w-5 h-5" />
              <p className="font-medium">Select a loan Payment from the Loan section first.</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loadingAllocations && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-center justify-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <p className="text-blue-700 font-medium">Loading payment history...</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <p className="font-medium">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Payment Complete Alert */}
        {isPaymentComplete && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <div>
                <p className="font-medium">Payment Already Complete</p>
                <p className="text-sm mt-1">This schedule has been fully paid. No further payment required.</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Panel - Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 md:p-8">
              {prefilledData && !isManualEntry && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Payment Information
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Borrower:</span>
                      <span className="font-medium ml-2">{prefilledData.borrowerName}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Loan:</span>
                      <span className="font-medium ml-2">{prefilledData.loanName}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Due Date:</span>
                      <span className="font-medium ml-2">
                        {new Date(prefilledData.dueDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Schedule ID:</span>
                      <span className="font-medium ml-2">{prefilledData.scheduleId}</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Payment History Summary */}
              {allocationHistory.length > 0 && !isManualEntry && !loadingAllocations && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Payment History Summary
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Previous Payments:</span>
                      <span className="font-medium ml-2">{fetchedPayments.length}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Total Paid:</span>
                      <span className="font-medium ml-2">{formatCurrency(totalPaidSoFar)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Remaining Total:</span>
                      <span className="font-medium ml-2 text-green-600">{formatCurrency(remainingTotalDue)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Remaining Fees:</span>
                      <span className="font-medium ml-2 text-green-600">{formatCurrency(remainingFeesDue)}</span>
                    </div>
                  </div>
                  {prefilledData && (
                    <div className="mt-3 pt-3 border-t border-amber-200 text-xs text-amber-700">
                      <p>Original: {formatCurrency(prefilledData.totalDue)} + {formatCurrency(prefilledData.feesDue)} = {formatCurrency(prefilledData.totalDue + prefilledData.feesDue)}</p>
                    </div>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Payment ID */}
                {/* <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FileText className="w-4 h-4" />
                    Payment ID
                  </label>
                  <input
                    type="text"
                    name="paymentId"
                    value={paymentData.paymentId}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="PAY-001"
                    required
                  />
                </div> */}

                {/* Schedule ID */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Calendar className="w-4 h-4" />
                    Schedule ID
                  </label>
                  <input
                    type="text"
                    name="scheduleId"
                    value={paymentData.scheduleId}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="SCH-001"
                    required
                    readOnly={!!prefilledData && !isManualEntry}
                  />
                </div>

                {/* Installment No */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <span className="font-bold">#</span>
                    Installment No
                  </label>
                  <input
                    type="text"
                    name="installmentNo"
                    value={paymentData.installmentNo}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="1"
                    min="1"
                    required
                    readOnly={!!prefilledData && !isManualEntry}
                  />
                </div>

                {/* Loan ID */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FileText className="w-4 h-4" />
                    Loan ID
                  </label>
                  <input
                    type="text"
                    name="loanId"
                    value={paymentData.loanId}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="LOAN-001"
                    required
                    readOnly={!!prefilledData && !isManualEntry}
                  />
                </div>

                {/* Monthly Amount */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <DollarSign className="w-4 h-4" />
                      Monthly Amount (Rs.)
                    </label>
                    {!isManualEntry && maxMonthlyAmount > 0 && (
                      <span className="text-xs text-gray-500">
                        Max: {formatCurrency(maxMonthlyAmount)}
                      </span>
                    )}
                  </div>
                  <input
                    type="number"
                    name="monthlyAmount"
                    value={paymentData.monthlyAmount}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="0.00"
                    min="0"
                    max={!isManualEntry ? maxMonthlyAmount : undefined}
                    step="0.01"
                    required
                    disabled={isPaymentComplete || loadingAllocations}
                  />
                </div>

                {/* Overdue Amount */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                      Overdue Amount (Rs.)
                    </label>
                    {!isManualEntry && maxOverdueAmount > 0 && (
                      <span className="text-xs text-gray-500">
                        Max: {formatCurrency(maxOverdueAmount)}
                      </span>
                    )}
                  </div>
                  <input
                    type="number"
                    name="overdueAmount"
                    value={paymentData.overdueAmount}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                    placeholder="0.00"
                    min="0"
                    max={!isManualEntry ? maxOverdueAmount : undefined}
                    step="0.01"
                    disabled={isPaymentComplete || loadingAllocations}
                    readOnly
                  />
                </div>

                {/* Payment Method */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <CreditCard className="w-4 h-4" />
                    Payment Method
                  </label>
                  <select
                    name="paymentMethod"
                    value={paymentData.paymentMethod}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 appearance-none bg-white"
                    required
                    disabled={isPaymentComplete || loadingAllocations}
                  >
                    {paymentMethods.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Reference No */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FileText className="w-4 h-4" />
                    Reference No
                  </label>
                  <input
                    type="text"
                    name="referenceNo"
                    value={paymentData.referenceNo}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="REF-001"
                    disabled={isPaymentComplete || loadingAllocations}
                  />
                </div>

                {/* Remarks */}
                <div className="md:col-span-2 space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FileText className="w-4 h-4" />
                    Remarks
                  </label>
                  <textarea
                    name="remarks"
                    value={paymentData.remarks}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                    rows="3"
                    placeholder="Additional notes or comments..."
                    disabled={isPaymentComplete || loadingAllocations}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="mt-8 flex justify-center">
                <button
                  type="submit"
                  disabled={isPaymentButtonDisabled()}
                  className={`px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl transform hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-3 ${isPaymentButtonDisabled() ? 'opacity-75 cursor-not-allowed' : 'hover:from-blue-700 hover:to-indigo-700'}`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-lg">Processing...</span>
                    </>
                  ) : isPaymentComplete ? (
                    <>
                      <Ban className="w-5 h-5" />
                      <span className="text-lg">Payment Complete</span>
                    </>
                  ) : loadingAllocations ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-lg">Loading...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-lg">Make Payment</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Right Panel - Summary */}
          <div className="lg:col-span-1">
            <div className="bg-blue-50 border border-blue-200 rounded-2xl shadow-xl p-6 md:p-8 sticky top-8">
              <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Calculator className="w-6 h-6 text-blue-600" />
                Payment Summary
              </h2>

              <div className="space-y-4">
                {/* Amount Breakdown */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div>
                      <span className="text-gray-600">Monthly Amount</span>
                      {!isManualEntry && (
                        <div className="text-xs text-gray-500">
                          {isPaymentComplete ? 'Paid in full' : `Remaining: ${formatCurrency(maxMonthlyAmount)}`}
                        </div>
                      )}
                    </div>
                    <span className="font-semibold text-gray-800">
                      {formatCurrency(paymentData.monthlyAmount || 0)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <div>
                      <span className="text-gray-600">Overdue Amount</span>
                      {!isManualEntry && (
                        <div className="text-xs text-gray-500">
                          {isPaymentComplete ? 'Paid in full' : `Remaining: ${formatCurrency(maxOverdueAmount)}`}
                        </div>
                      )}
                    </div>
                    <span className="font-semibold text-gray-800">
                      {formatCurrency(paymentData.overdueAmount || 0)}
                    </span>
                  </div>

                  <div className="border-t border-blue-200 pt-3">
                    <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg border border-blue-200">
                      <span className="text-lg font-bold text-gray-800">Total Amount</span>
                      <span className="text-2xl font-bold text-blue-600">
                        {formatCurrency(paymentData.totalAmount || 0)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payment Status */}
                <div className={`mt-6 p-4 rounded-lg border ${
                  isPaymentComplete 
                    ? 'bg-green-50 border-green-300' 
                    : (parseFloat(paymentData.totalAmount) || 0) <= 0 
                      ? 'bg-red-50 border-red-300' 
                      : 'bg-blue-50 border-blue-300'
                }`}>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    {isPaymentComplete ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (parseFloat(paymentData.totalAmount) || 0) <= 0 ? (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-blue-500" />
                    )}
                    <span>Payment Status</span>
                  </h4>
                  <p className={`text-sm ${
                    isPaymentComplete 
                      ? 'text-green-600' 
                      : (parseFloat(paymentData.totalAmount) || 0) <= 0 
                        ? 'text-red-600' 
                        : 'text-blue-600'
                  }`}>
                    {isPaymentComplete 
                      ? 'Payment already completed for this schedule' 
                      : (parseFloat(paymentData.totalAmount) || 0) <= 0 
                        ? 'Payment amount must be greater than zero' 
                        : 'Ready to submit payment'}
                  </p>
                </div>

                {/* Important Notes */}
                <div className="mt-6 p-4 bg-blue-100 rounded-lg border border-blue-300">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Important Notes 
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="mt-1">•</span>
                      <span>Go to a Loans section</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1">•</span>
                      <span>Open Loan Schedule via View Details</span>
                    </li>
                     <li className="flex items-start gap-2">
                      <span className="mt-1">•</span>
                      <span>Click Make Payment button</span>
                    </li>
                    {!isManualEntry && (
                      <>
                        <li className="flex items-start gap-2">
                          <span className="mt-1">•</span>
                          <span>Amounts are calculated based on previous payments</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1">•</span>
                          <span>You cannot pay more than the remaining balance</span>
                        </li>
                        <li className="flex items-start gap-2">
                      <span className="mt-1">•</span>
                      <span>Payment must be greater than zero</span>
                    </li>
                      </>
                    )}
                    
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MakePayment;